import { toast } from "sonner";

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

// Default request timeout (2026-11-03). Until now apiFetch/apiFetchBlob carried
// NO timeout — a hung Worker or DB (the 2026-09-21 6h Hyperdrive connect-timeout
// outage class) left every query and mutation spinner stuck indefinitely with no
// error state and no user signal. 30s is generous for every regular route: the
// Worker is Smart-Placed in SIN next to the DB (~1-5ms round trips, §13), and the
// slowest regular path (backups proxy) is itself capped at 15s server-side
// (BACKUP_FETCH_TIMEOUT_MS in lib/backupFetch.ts).
//
// On timeout, fetch rejects with a TimeoutError DOMException — the SAME failure
// path as a network error: TanStack Query moves the query to error state (with
// its retry: 1), mutations toast their fallback message via lib/mutations.ts
// onError, and autosave toasts "Failed to autosave …". No new handling needed.
//
// A caller-provided init.signal stays authoritative — the default is applied
// only when absent.
const DEFAULT_TIMEOUT_MS = 30_000;

// AI routes (2026-11-03): the server proxies OpenRouter with its own per-route
// caps — chat 60s default (callOpenRouterChat), STT 120s (openrouter.ts
// transcribe), suggest-assignments 180s (routes/ai.ts AbortSignal.timeout).
// The client timeout must EXCEED the slowest server cap (180s) or it would
// abort a still-working transcription/suggestion and surface a failure the
// server never had. 200s gives headroom over the 180s worst case.
const AI_TIMEOUT_MS = 200_000;

function timeoutSignalFor(path: string): AbortSignal {
  return AbortSignal.timeout(path.startsWith("/ai/") ? AI_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);
}

// 503 service_unavailable handling (2026-09-22, Layer 7 of the Hyperdrive
// outage hardening — see SCHEMA.md §18 2026-09-21 postmortem). When the DB
// path fails on the Worker (Hyperdrive connect-timeout, Postgres error),
// authMiddleware and the login route both return 503 with a clear message.
// Without this toast, TanStack Query would surface a generic "API 503"
// error and the user would have no idea it's a transient infra issue.
// The toast fires once per 503 (sonner dedupes by default via the message
// string), so a burst of failed requests doesn't spam the UI.
const SERVICE_UNAVAILABLE_TOAST = "Service temporarily unavailable — please try again in a few minutes";

function notifyServiceUnavailable(): void {
  toast.error(SERVICE_UNAVAILABLE_TOAST, { duration: 8000 });
}

// 401 visibility net (2026-09-23). During that day's incident every apiFetch
// went out with NO Authorization header (session secret wiped from storage):
// the drug dropdown silently rendered empty, and the user's only signal was
// "nothing comes up". Sonner dedupes by message so a burst of 401s toasts
// once, not per request.
const UNAUTHORIZED_TOAST = "Not signed in — your session has expired. Please sign in again.";

let lastUnauthorizedToastAt = 0;
function notifyUnauthorized(): void {
  // Throttle to once per 30s even if sonner's message-dedupe is bypassed by
  // a re-render (cheap belt-and-suspenders; costs nothing).
  const now = Date.now();
  if (now - lastUnauthorizedToastAt < 30_000) return;
  lastUnauthorizedToastAt = now;
  toast.error(UNAUTHORIZED_TOAST, { duration: 8000 });
}

// --- Session transport: httpOnly cookie (2026-09-24, Appwrite SSR pattern) --
// The Appwrite session secret now lives ONLY in an httpOnly Secure
// SameSite=Strict cookie set by POST /auth/login (a_session_<project>,
// Path=/ — root paths in prod; the Vite /api proxy strips the prefix in
// dev) — JavaScript can neither read nor write it, so the entire
// 2026-09-23 localStorage-wipe incident class (SDK cookieFallback overwrite,
// storage quota failures, cross-tab clobber) is structurally impossible.
// /auth/refresh, /auth/logout, and the change-password session sweep prove
// possession server-side by reading the cookie; the browser attaches it to
// every same-origin /api request automatically (default fetch credentials
// for same-origin).
//
// MIGRATION CLEANUP: pre-deploy bundles stored the secret under
// `emr:sessionSecret` (and, before that, inside the SDK's `cookieFallback`).
// signOut() sweeps both leftovers below so no credential remains in
// localStorage after a sign-out. No read path exists anymore.

/**
 * Sweep leftover session-secret entries from pre-cookie-deploy bundles
 * (sign-out housekeeping — see header note). Best-effort: failures are
 * ignored (the entries are stale credentials of a session that is already
 * dead server-side; the httpOnly cookie itself is cleared by the server).
 */
export function sweepLegacySessionSecretStorage(): void {
  try {
    localStorage.removeItem("emr:sessionSecret");
  } catch {
    // ignore
  }
  try {
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID as string | undefined;
    const legacyKey = `a_session_${projectId ?? ""}`;
    const fallback = JSON.parse(localStorage.getItem("cookieFallback") ?? "{}") as Record<string, string>;
    if (legacyKey in fallback) {
      delete fallback[legacyKey];
      localStorage.setItem("cookieFallback", JSON.stringify(fallback));
    }
  } catch {
    // ignore
  }
}

// --- JWT reuse + proactive re-mint ----------------------------------------
// The API JWT is minted by the Worker (/auth/login on sign-in, /auth/refresh
// for re-mints) and is valid for 30 minutes (Appwrite `duration: 1800`).
// It is cached in memory and cleared on 401 (the session may have been
// revoked server-side) and on explicit sign-out.
//
// Two re-mint mechanisms keep the token fresh:
//
// 1. PROACTIVE (primary): a background timer fires ~10 min before the JWT's
//    exp, mints a fresh token, and atomically swaps cachedJwt. The user's
//    requests never block on a re-mint — they always find a fresh JWT in
//    the cache. With a 10-min window before expiry, transient DB/Hyperdrive
//    blips (which typically last seconds, not 10+ min) are retried without
//    the user ever seeing a stale token. This was the root-cause fix for the
//    "random logout after ~30 min" symptom (2026-09-22): the old on-demand
//    re-mint fired ~30s before expiry — a single DB error in that window
//    discarded the still-valid JWT and 401'd every subsequent request.
//
// 2. ON-DEMAND (fallback): getJwt() re-mints if the cached JWT is within 30s
//    of expiry (e.g. after the tab was backgrounded and the proactive timer
//    was throttled). The on-demand path has a 503/transient-failure guard
//    (2026-09-22): it keeps the old JWT on transient failures instead of
//    discarding it, so the user keeps working until the next retry.
//
// NOTE (2026-09-21): the re-mint deliberately does NOT use the browser
// Appwrite SDK's account.createJWT(). Legacy browser-direct logins left a
// 1-year a_session_* cookie on fra.cloud.appwrite.io, and the SDK always
// sends credentials:'include' — Appwrite prioritizes the (possibly dead)
// Cookie over the valid X-Fallback-Cookies secret, so every SDK call 401'd
// (silently for re-mints, causing the 30-minute forced re-login; see
// SCHEMA.md §18). The Worker path has no cookie jar and cannot be shadowed.
let cachedJwt: { token: string; expiresAtMs: number } | null = null;
let jwtMintInFlight: Promise<string | null> | null = null;
const JWT_RENEW_MARGIN_MS = 30_000; // on-demand re-mint 30s before exp (fallback)

// Dead-session circuit breaker (2026-10-24, post-mortem #246). A definitive
// 401/403 from /auth/refresh means the session cookie is absent or dead —
// no mint attempt can succeed until the user signs in again. A 429 means
// this client already made 30 refresh attempts in the last minute (per
// isolate) — the server tells us when the bucket resets. Without this
// breaker, a signed-out tab re-attempts the mint on EVERY apiFetch (every
// query mount, retry, and refetch), measured at ~137 requests/min from a
// single browser for 16 minutes (2026-10-23 04:16-04:32 UTC — 2,253
// error_log rows) and ~8/sec for 3 minutes (10:06-10:09 — 1,277 429 rows)
// until the user finally logged in. While the cooldown holds, getJwt skips
// the network round trip entirely and returns null (requests go out without
// a token and surface the same 401 UX). The breaker clears on seedCachedJwt
// (login / restore) and simply expires otherwise — the next apiFetch after
// the window retries naturally, so a session created in another tab is
// picked up within the cooldown. Transient failures (503/5xx/network/
// timeout) do NOT trip the breaker — the keep-old-JWT logic below already
// handles those.
const AUTH_FAILURE_COOLDOWN_MS = 30_000;
let authRetryNotBeforeMs = 0;

// Proactive re-mint: fire 10 min before the JWT's exp (leaves a 10-min window
// to retry on transient failures before the old JWT actually expires).
const JWT_REFRESH_LEAD_MS = 10 * 60 * 1000;
// Retry interval after a transient proactive re-mint failure (1 min — gives
// the DB/Hyperdrive time to recover while the old JWT is still valid).
const JWT_REFRESH_RETRY_MS = 60 * 1000;
let jwtRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function decodeJwtExpMs(jwt: string): number | null {
  try {
    const b64 = jwt.split(".")[1]?.replace(/-/g, "+").replace(/_/g, "/");
    if (!b64) return null;
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Low-level /auth/refresh call — mints a fresh JWT via the Worker. The
 * session secret rides in the httpOnly cookie (set at login; the browser
 * attaches it automatically — credentials:"include" makes that explicit for
 * the Vite dev proxy too). Returns { jwt, expMs } on success, or null on
 * any failure. On 401/403 (genuine session death) there is nothing local to
 * clear — the cookie is server-managed; the signed-out state is reached by
 * the user signing in again. Does NOT touch cachedJwt — the caller decides
 * how to handle the result.
 */
/**
 * Attempt a JWT re-mint via POST /auth/refresh.
 * Returns:
 *   { jwt, expMs }              — success
 *   { retryAfterMs }            — definitive 401/403 (dead/absent session;
 *                                  retrying cannot succeed until sign-in) or
 *                                  429 (server-side limiter — retry when it
 *                                  says the bucket resets)
 *   null                         — transient failure (503/5xx/network/
 *                                  timeout): retrying may succeed
 */
async function refreshJwt(): Promise<
  { jwt: string; expMs: number } | { retryAfterMs: number } | null
> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Same-origin relative URL + credentials → the httpOnly session cookie
      // flows automatically (prod: same Worker origin; dev: Vite /api proxy
      // forwards Cookie headers to localhost:8787).
      credentials: "include",
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(10_000),
    });
    if (res.status === 401 || res.status === 403) {
      // Dead/absent session — no retry can succeed until a new sign-in.
      return { retryAfterMs: AUTH_FAILURE_COOLDOWN_MS };
    }
    if (res.status === 429) {
      // Server-side limiter (30/min per session-key per isolate) — honor
      // its retry hint so this client stops feeding the storm it caused.
      const json = (await res.json().catch(() => ({}))) as { retryAfterMs?: number };
      return { retryAfterMs: Math.min(Math.max(json.retryAfterMs ?? 30_000, 1_000), 120_000) };
    }
    if (!res.ok) {
      return null; // transient (503/500)
    }
    const json = (await res.json()) as { jwt: string };
    const expMs = decodeJwtExpMs(json.jwt) ?? Date.now() + 60_000;
    return { jwt: json.jwt, expMs };
  } catch {
    return null; // network error / timeout
  }
}

async function getJwt(): Promise<string | null> {
  if (cachedJwt && cachedJwt.expiresAtMs - JWT_RENEW_MARGIN_MS > Date.now()) {
    return cachedJwt.token;
  }
  // Dead-session / limiter circuit breaker (2026-10-24): a recent
  // definitive 401/403 (or a 429 whose retry hint hasn't elapsed) means no
  // mint can succeed now — skip the network round trip and return null.
  // The breaker expires with its cooldown, and seedCachedJwt (login /
  // restore) clears it immediately.
  if (authRetryNotBeforeMs && Date.now() < authRetryNotBeforeMs) {
    return null;
  }
  // On-demand fallback: the proactive timer didn't fire (backgrounded tab,
  // first load, or it failed). Re-mint synchronously with the 503 guard.
  const prevJwt = cachedJwt;
  cachedJwt = null;
  if (!jwtMintInFlight) {
    jwtMintInFlight = (async () => {
      try {
        // No local secret exists (httpOnly cookie transport) — the server
        // resolves the session from the cookie. A 401 means "not signed in
        // (or session dead)": null falls through and public routes still
        // work; apiFetch's 401-retry + notifyUnauthorized cover authed ones.
        const result = await refreshJwt();
        if (!result) {
          // Transient failure (503/500/network/timeout) — keep the old
          // JWT for the remaining margin window so the user keeps working.
          // The old JWT will be re-checked against its real exp on the next
          // getJwt call; if it has actually expired, the Worker's
          // authMiddleware returns 401 and apiFetch's 401-retry clears the
          // cache and re-mints once.
          if (prevJwt) {
            cachedJwt = prevJwt;
            return prevJwt.token;
          }
          return null;
        }
        if ("retryAfterMs" in result) {
          // Definitive 401/403 (dead/absent session) or 429 (limiter) —
          // trip the circuit breaker so this tab stops hammering
          // /auth/refresh until the cooldown elapses or a new sign-in
          // clears it.
          authRetryNotBeforeMs = Date.now() + result.retryAfterMs;
          return null;
        }
        cachedJwt = { token: result.jwt, expiresAtMs: result.expMs };
        // Schedule the next proactive refresh for the new JWT.
        scheduleJwtRefresh(result.expMs);
        return result.jwt;
      } finally {
        jwtMintInFlight = null;
      }
    })();
  }
  return jwtMintInFlight;
}
export { getJwt };

/**
 * Proactively re-mint the JWT in the background (called by the refresh timer
 * ~10 min before expiry). This is the primary re-mint mechanism — the user's
 * requests never block on a /auth/refresh round trip. On success, the new
 * JWT atomically replaces cachedJwt and the next refresh is scheduled. On
 * transient failure, the old JWT is kept (still valid for ~10 more min) and
 * the timer retries in 1 min. On 401/403 the session is dead — the user will
 * be prompted to re-login on the next request.
 */
async function proactiveJwtRefresh(): Promise<void> {
  if (!cachedJwt) return; // cache cleared (sign-out or 401) — timer cancelled by clearCachedJwt

  // Defer to the on-demand single-flight if one is already in progress —
  // avoids a double /auth/refresh call (getJwt's jwtMintInFlight).
  if (jwtMintInFlight) {
    await jwtMintInFlight;
    if (cachedJwt) scheduleJwtRefresh(cachedJwt.expiresAtMs);
    return;
  }

  const result = await refreshJwt();
  if (result && !("retryAfterMs" in result)) {
    // Success — swap in the new JWT and schedule the next refresh.
    cachedJwt = { token: result.jwt, expiresAtMs: result.expMs };
    scheduleJwtRefresh(result.expMs);
  } else if (result && "retryAfterMs" in result) {
    // Definitive 401/403 or 429 — trip the breaker; the timer was already
    // cancelled by clearCachedJwt (cachedJwt is null here), nothing further
    // to schedule.
    authRetryNotBeforeMs = Date.now() + result.retryAfterMs;
  } else if (cachedJwt) {
    // Transient failure — keep the old JWT (still valid for ~10 min) and
    // retry in 1 min. The old JWT is served by getJwt until it expires.
    scheduleJwtRefresh(cachedJwt.expiresAtMs, JWT_REFRESH_RETRY_MS);
  }
  // If !result && !cachedJwt, a transient failure hit a cleared cache —
  // nothing to schedule; the next apiFetch re-mints on demand.
}

/**
 * Schedule the proactive JWT re-mint. Fires `leadMs` before the JWT's exp
 * (default 10 min), giving a wide window to retry on transient failures
 * before the old JWT actually expires. Cancels any previously scheduled
 * timer. Called by seedCachedJwt (after login/restore) and after each
 * successful re-mint.
 */
function scheduleJwtRefresh(
  expiresAtMs: number,
  leadMs: number = JWT_REFRESH_LEAD_MS,
): void {
  if (jwtRefreshTimer !== null) {
    clearTimeout(jwtRefreshTimer);
    jwtRefreshTimer = null;
  }
  const delay = expiresAtMs - leadMs - Date.now();
  if (delay <= 0) {
    // Already past the lead time — refresh now (the old JWT may still be
    // valid for a few seconds; the proactive refresh mints a new one).
    void proactiveJwtRefresh();
    return;
  }
  jwtRefreshTimer = setTimeout(() => {
    void proactiveJwtRefresh();
  }, delay);
}

/** Clear any cached JWT (after sign-out, or a hard 401 from the API). */
export function clearCachedJwt(): void {
  cachedJwt = null;
  if (jwtRefreshTimer !== null) {
    clearTimeout(jwtRefreshTimer);
    jwtRefreshTimer = null;
  }
}

/**
 * Seed the JWT cache with a token minted server-side (POST /auth/login
 * returns a ready-to-use JWT). Decodes its exp so the normal expiry-margin
 * logic applies. Schedules a proactive re-mint ~10 min before exp. No-op if
 * the token is undecodable. Clears the dead-session circuit breaker — a
 * successful sign-in/restore is the definitive "session alive" signal.
 */
export function seedCachedJwt(jwt: string): void {
  const expMs = decodeJwtExpMs(jwt);
  if (expMs) {
    authRetryNotBeforeMs = 0;
    cachedJwt = { token: jwt, expiresAtMs: expMs };
    scheduleJwtRefresh(expMs);
  }
}

/**
 * Tiny fetch wrapper that attaches the Appwrite JWT as a Bearer token
 * and throws on non-2xx with the server error body.
 *
 * The JWT is cached for its 30-minute lifespan (see above) and proactively
 * re-minted ~10 min before expiry by a background timer (scheduleJwtRefresh).
 * The on-demand re-mint in getJwt is a fallback for when the proactive timer
 * didn't fire (backgrounded tab, first load). On a 401 with a cached token the
 * cache is cleared and the request retried once with a fresh token.
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
  _isRetry = false,
): Promise<T> {
  const token = await getJwt();

  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    // Default timeout (2026-11-03) — see DEFAULT_TIMEOUT_MS note above. A
    // caller-provided init.signal (or its absence) stays authoritative.
    signal: init.signal ?? timeoutSignalFor(path),
  });

  // Surface a common misconfiguration loudly: the SPA is being served from an
  // origin that has no API behind it (e.g. `npx serve apps/web/dist` on a random
  // port). The request hits the SPA's own index.html fallback → 404 with an
  // HTML body → TanStack Query errors silently → empty UI / stuck loaders.
  // Detect this and throw a clear, actionable error instead of a generic 404.
  if (res.status === 404) {
    const ctype = res.headers.get("content-type") ?? "";
    if (ctype.includes("text/html")) {
      const origin = typeof location !== "undefined" ? location.origin : "(unknown)";
      console.error(
        `[apiFetch] ${path} returned HTML (not JSON) from ${origin}${API_URL}.\n` +
          `This usually means the SPA is being served from an origin with no API backend. ` +
          `For production open the deployed Worker URL; for local dev run \`pnpm dev\` and open http://localhost:5173.`,
      );
      const err = new Error(
        `API not reachable at ${origin}${API_URL}${path} — the server returned the SPA HTML. ` +
          `Serve the app from the API origin (live Worker) or run \`pnpm dev\`.`,
      ) as Error & { status: number; body: unknown };
      err.status = 404;
      err.body = null;
      throw err;
    }
  }

  if (!res.ok) {
    // A 401 with a cached JWT means the token went stale early (session revoked
    // server-side or exp misread). Clear the cache and retry ONCE with a fresh
    // mint; a second 401 propagates to the caller (genuine auth problem).
    if (res.status === 401 && token && !_isRetry) {
      clearCachedJwt();
      return apiFetch<T>(path, init, true);
    }
    // 401 visibility net (2026-09-23): a bare 401 (no JWT at all — session
    // secret missing) previously failed silently: the drug dropdown just
    // rendered empty with zero user-visible signal. Toast once per burst so
    // the user knows the app is failing auth — "sign in again" is the fix.
    if (res.status === 401) {
      notifyUnauthorized();
    }
    // 503 = DB/Hyperdrive trouble on the Worker (authMiddleware or login
    // route caught a PostgresError and returned service_unavailable). Toast
    // once so the user knows it's transient infra, not a bug. The error still
    // propagates so TanStack Query can show its retry affordance.
    if (res.status === 503) {
      notifyServiceUnavailable();
    }
    let body: unknown;
    // Clone before any body read so a failed .json() doesn't consume the stream.
    try {
      body = await res.clone().json();
    } catch {
      try {
        body = await res.text();
      } catch {
        // Neither JSON nor text — leave undefined; err.body consumers
        // already treat missing body as unknown.
        body = undefined;
      }
    }
    const err = new Error(`API ${res.status}: ${path}`) as Error & {
      status: number;
      body: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Same as apiFetch but returns the response body as a Blob — for binary
 * downloads (e.g. the xlsx import template) that must still carry the
 * Appwrite JWT header. window.open() can't attach headers, so authenticated
 * file routes must be fetched this way and turned into an object URL.
 */
export async function apiFetchBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const token = await getJwt();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    signal: init.signal ?? timeoutSignalFor(path),
  });
  if (!res.ok) {
    if (res.status === 503) {
      notifyServiceUnavailable();
    }
    const err = new Error(`API ${res.status}: ${path}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    try {
      err.body = await res.clone().json();
    } catch {
      err.body = null;
    }
    throw err;
  }
  return res.blob();
}
